import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { AlertTriangle, ArrowLeft, Loader2, Network } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANY = '*';
const WIDTH = 1000;
const HEIGHT = 560;
const CATEGORY_COLOR = '#ea580c';
const ROUTE_COLOR = '#16a34a';
const UNIT_CATEGORY_COLORS = ['#3b82f6', '#a855f7', '#14b8a6', '#eab308', '#ec4899', '#6366f1', '#84cc16', '#f97316'];

const formatFactor = (n) => String(n);   // exactly as stored
const edgeKey = (kind, a, b) => `${kind}:${[a, b].sort().join('|')}`;

// Fruchterman-Reingold layout: connected units pull together, all units push apart. Deterministic (fixed start).
function layoutGraph(nodes, edges) {
  const pos = {};
  nodes.forEach((node, i) => {
    const angle = (2 * Math.PI * i) / nodes.length;
    pos[node.unit] = { x: WIDTH / 2 + Math.cos(angle) * WIDTH / 3, y: HEIGHT / 2 + Math.sin(angle) * HEIGHT / 3 };
  });
  const k = Math.sqrt((WIDTH * HEIGHT) / Math.max(nodes.length, 1)) * 0.75;
  const iterations = 300;
  for (let iter = 0; iter < iterations; iter++) {
    const temperature = (1 - iter / iterations) * (WIDTH / 8);
    const move = {};
    nodes.forEach(n => { move[n.unit] = { x: 0, y: 0 }; });
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = pos[nodes[i].unit], b = pos[nodes[j].unit];
        let dx = a.x - b.x, dy = a.y - b.y;
        const dist = Math.max(Math.hypot(dx, dy), 0.01);
        const force = (k * k) / dist;
        dx = (dx / dist) * force; dy = (dy / dist) * force;
        move[nodes[i].unit].x += dx; move[nodes[i].unit].y += dy;
        move[nodes[j].unit].x -= dx; move[nodes[j].unit].y -= dy;
      }
    }
    edges.forEach(({ from, to }) => {
      const a = pos[from], b = pos[to];
      if (!a || !b) return;
      let dx = a.x - b.x, dy = a.y - b.y;
      const dist = Math.max(Math.hypot(dx, dy), 0.01);
      const force = (dist * dist) / k;
      dx = (dx / dist) * force; dy = (dy / dist) * force;
      move[from].x -= dx; move[from].y -= dy;
      move[to].x += dx; move[to].y += dy;
    });
    nodes.forEach(({ unit }) => {
      const m = move[unit];
      const len = Math.max(Math.hypot(m.x, m.y), 0.01);
      const p = pos[unit];
      p.x = Math.min(WIDTH - 50, Math.max(50, p.x + (m.x / len) * Math.min(len, temperature)));
      p.y = Math.min(HEIGHT - 30, Math.max(30, p.y + (m.y / len) * Math.min(len, temperature)));
    });
  }
  return pos;
}

const LoadingSpinner = () => <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0 mr-1" data-testid="dropdown-loading" />;

const routeTitle = (route, index) =>
  route.kind === 'unit_only' ? `Route ${index + 1} · unit conversions only` : `Route ${index + 1} · via category conversion #${route.category_conversion_id}`;

// Why the portal can't convert the chosen pair, or null when it can (or no pair is chosen)
const unreachableMessage = (graph, fromUnit, toUnit) => {
  const from = fromUnit.toUpperCase(), to = toUnit.toUpperCase();
  if (graph.route_status === 'needs_multiple_category_conversions') {
    const { steps, category_steps: categorySteps } = graph.blocked_route;
    const path = [steps[0].from, ...steps.map(s => s.to)].join(' → ');
    return {
      title: `The portal can't convert ${from} to ${to} for this activity`,
      text: `These units are only connected by chaining ${categorySteps} category conversions (${path}). The portal uses at most one category conversion per route, so it can't go through it. Add a direct category or unit conversion between these units to make it work.`,
      toastTitle: `Can't convert ${from} → ${to}`,
      toastText: `Needs ${categorySteps} category conversions in a row (${path}). The portal only uses one per route.`,
    };
  }
  if (graph.route_status === 'no_connection') {
    return {
      title: `The portal can't convert ${from} to ${to} for this activity`,
      text: `No unit conversions or category conversions connect ${from} and ${to} (for example, energy and volume units are not linked unless a category conversion joins them).`,
      toastTitle: `Can't convert ${from} → ${to}`,
      toastText: `No unit or category conversion connects these units for this activity.`,
    };
  }
  return null;
};

const routePath = (route) => [route.steps[0]?.from, ...route.steps.map(s => s.to)].filter(Boolean);

export default function ConversionGraphPage() {
  const navigate = useNavigate();
  const [options, setOptions] = useState({ combinations: [], units: [] });
  const [subCategory, setSubCategory] = useState('');
  const [type, setType] = useState(ANY);
  const [subType, setSubType] = useState(ANY);
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(0);

  useEffect(() => {
    axios.get(`${API_URL}/conversion-graph/options`)
      .then(res => setOptions(res.data))
      .catch(() => toast.error('Failed to load options'))
      .finally(() => setOptionsLoading(false));
  }, []);

  const subCategories = useMemo(() => [...new Set(options.combinations.map(c => c.sub_category))], [options]);
  const types = useMemo(
    () => [...new Set(options.combinations.filter(c => c.sub_category === subCategory).map(c => c.type))].filter(t => t !== ANY),
    [options, subCategory]);
  const subTypes = useMemo(
    () => [...new Set(options.combinations.filter(c => c.sub_category === subCategory && c.type === type).map(c => c.sub_type))].filter(t => t !== ANY),
    [options, subCategory, type]);

  useEffect(() => {
    if (!subCategory) return;
    setLoading(true);
    const controller = new AbortController();
    axios.get(`${API_URL}/conversion-graph`, {
      params: { sub_category: subCategory, type, sub_type: subType, ...(fromUnit && toUnit && { from_unit: fromUnit, to_unit: toUnit }) },
      signal: controller.signal,
    })
      .then(res => {
        setGraph(res.data);
        setSelectedRoute(Math.max(0, res.data.routes.findIndex(r => r.shortest)));
        const problem = fromUnit && toUnit ? unreachableMessage(res.data, fromUnit, toUnit) : null;
        if (problem) toast.warning(problem.toastTitle, { description: problem.toastText, duration: 8000 });
      })
      .catch((error) => { if (!axios.isCancel(error)) toast.error('Failed to load the conversion graph'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [subCategory, type, subType, fromUnit, toUnit]);

  // The unit lists come with the graph, so they are loading whenever the graph is
  const unitsLoading = !!subCategory && loading;

  // From / To only offer units connected to this activity. Any pair can be chosen: if the portal can't convert it,
  // the Routes section says why.
  const fromOptions = useMemo(() => (graph?.available_units ?? []).filter(u => u !== toUnit), [graph, toUnit]);
  const toOptions = useMemo(() => (graph?.available_units ?? []).filter(u => u !== fromUnit), [graph, fromUnit]);

  // A choice that is no longer connected to the activity after changing it is cleared
  useEffect(() => {
    if (!graph) return;
    if (fromUnit && !graph.available_units.includes(fromUnit)) setFromUnit('');
    if (toUnit && !graph.available_units.includes(toUnit)) setToUnit('');
  }, [graph]); // eslint-disable-line react-hooks/exhaustive-deps

  // The layout only depends on which units and conversions are drawn, so choosing From / To units (same drawing) reuses it
  const graphRef = useRef(null);
  graphRef.current = graph;
  const drawingKey = graph
    ? [graph.nodes.map(n => n.unit).join(','), graph.unit_edges.map(e => e.id).join(','), graph.category_edges.map(e => e.id).join(',')].join('|')
    : '';
  const positions = useMemo(() => {
    const g = graphRef.current;
    return g ? layoutGraph(g.nodes, [...g.unit_edges, ...g.category_edges]) : {};
  }, [drawingKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const categoryColors = useMemo(() => {
    const unitCategories = [...new Set((graph?.nodes || []).map(n => n.unit_category).filter(Boolean))].sort();
    return Object.fromEntries(unitCategories.map((c, i) => [c, UNIT_CATEGORY_COLORS[i % UNIT_CATEGORY_COLORS.length]]));
  }, [graph]);

  const route = graph?.routes[selectedRoute];
  const highlightedEdges = new Set((route?.steps || []).map(s => edgeKey(s.kind, s.from, s.to)));
  const highlightedNodes = new Set(route ? routePath(route) : []);
  const categoryEndpoints = new Set((graph?.category_edges || []).flatMap(e => [e.from, e.to]));

  const drawEdge = (edge, kind) => {
    const a = positions[edge.from], b = positions[edge.to];
    if (!a || !b) return null;
    const highlighted = highlightedEdges.has(edgeKey(kind, edge.from, edge.to));
    const color = highlighted ? ROUTE_COLOR : kind === 'category' ? CATEGORY_COLOR : '#cbd5e1';
    return (
      <g key={`${kind}-${edge.id}-${edge.from}-${edge.to}`}>
        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={color} strokeWidth={highlighted ? 4 : kind === 'category' ? 3 : 1.5} strokeDasharray={kind === 'category' && !highlighted ? '6 4' : undefined} />
        <title>{kind === 'category' ? `Category conversion #${edge.id}: 1 ${edge.from} = ${edge.value} ${edge.to}` : `Unit conversion #${edge.id}: 1 ${edge.from} = ${edge.value} ${edge.to}`}</title>
        {(highlighted || kind === 'category') && (
          <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 4} textAnchor="middle" fontSize="11" fontWeight="600" fill={highlighted ? ROUTE_COLOR : CATEGORY_COLOR} stroke="white" strokeWidth="3" paintOrder="stroke">
            ×{formatFactor(edge.value)}
          </text>
        )}
      </g>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="conversion-graph-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/category-conversions')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Conversion Graph</h1>
            <p className="text-muted-foreground mt-1">How the portal converts units for a sub category: unit conversions plus category conversions, and every route between two units</p>
          </div>
        </div>

        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Network className="w-5 h-5" />Activity and units</CardTitle>
            <CardDescription>Pick the sub category (type and sub type are optional, like in the portal); the unit lists then only offer units connected to it. Pick two units to see the routes between them, or why there is none</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Sub Category</Label>
                <Select value={subCategory} onValueChange={(v) => { setSubCategory(v); setType(ANY); setSubType(ANY); }} disabled={optionsLoading}>
                  <SelectTrigger data-testid="graph-sub-category">
                    <SelectValue placeholder={optionsLoading ? 'Loading sub categories…' : 'Select sub category'} />
                    {optionsLoading && <LoadingSpinner />}
                  </SelectTrigger>
                  <SelectContent>{subCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={type} onValueChange={(v) => { setType(v); setSubType(ANY); }} disabled={!subCategory}>
                  <SelectTrigger data-testid="graph-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>Any (*)</SelectItem>
                    {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sub Type</Label>
                <Select value={subType} onValueChange={setSubType} disabled={!subCategory}>
                  <SelectTrigger data-testid="graph-sub-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>Any (*)</SelectItem>
                    {subTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>From Unit</Label>
                <Select value={fromUnit} onValueChange={setFromUnit} disabled={!subCategory || unitsLoading}>
                  <SelectTrigger data-testid="graph-from-unit">
                    <SelectValue placeholder={unitsLoading ? 'Loading units…' : 'Select unit'} />
                    {unitsLoading && <LoadingSpinner />}
                  </SelectTrigger>
                  <SelectContent>{fromOptions.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>To Unit</Label>
                <Select value={toUnit} onValueChange={setToUnit} disabled={!subCategory || unitsLoading}>
                  <SelectTrigger data-testid="graph-to-unit">
                    <SelectValue placeholder={unitsLoading ? 'Loading units…' : 'Select unit'} />
                    {unitsLoading && <LoadingSpinner />}
                  </SelectTrigger>
                  <SelectContent>{toOptions.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                {(fromUnit || toUnit) && <Button variant="outline" onClick={() => { setFromUnit(''); setToUnit(''); }}>Clear units</Button>}
              </div>
            </div>
          </CardContent>
        </Card>

        {!subCategory && <p className="text-center text-muted-foreground py-12">Select a sub category to see its conversion graph</p>}
        {subCategory && loading && !graph && <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}

        {subCategory && graph && (
          <>
            <Card className="border-gray-200" data-testid="graph-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Graph</CardTitle>
                <CardDescription>
                  {graph.matched
                    ? `Category conversions matched at "${graph.matched.level}" (${graph.matched.sub_category} / ${graph.matched.type} / ${graph.matched.sub_type}). The portal uses the first level that has any.`
                    : 'No category conversion matches this activity (exact, sub type *, or type *), so only unit conversions apply.'}
                </CardDescription>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2">
                  <span className="flex items-center gap-1"><span className="inline-block w-6 border-t-2" style={{ borderColor: '#cbd5e1' }} />Unit conversion (works both ways)</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-6 border-t-2 border-dashed" style={{ borderColor: CATEGORY_COLOR }} />Category conversion</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-6 border-t-4" style={{ borderColor: ROUTE_COLOR }} />Selected route</span>
                  {Object.entries(categoryColors).map(([c, color]) => (
                    <span key={c} className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />{c}</span>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {loading && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
                <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full min-w-[700px] border rounded-lg bg-white" role="img" aria-label="Conversion graph">
                  {graph.unit_edges.map(e => drawEdge(e, 'unit'))}
                  {graph.category_edges.map(e => drawEdge(e, 'category'))}
                  {graph.nodes.map(node => {
                    const p = positions[node.unit];
                    if (!p) return null;
                    const isRouteNode = highlightedNodes.has(node.unit);
                    const isEnd = node.unit === fromUnit.toUpperCase() || node.unit === toUnit.toUpperCase();
                    return (
                      <g key={node.unit} data-testid={`node-${node.unit}`}>
                        <circle cx={p.x} cy={p.y} r={isEnd ? 12 : 9} fill={categoryColors[node.unit_category] || '#94a3b8'}
                          stroke={isRouteNode ? ROUTE_COLOR : categoryEndpoints.has(node.unit) ? CATEGORY_COLOR : 'white'} strokeWidth={isRouteNode || categoryEndpoints.has(node.unit) ? 3 : 2} />
                        <text x={p.x} y={p.y - 15} textAnchor="middle" fontSize="11" fontWeight={isRouteNode ? 700 : 500} fill="#111827" stroke="white" strokeWidth="3" paintOrder="stroke">{node.unit}</text>
                        <title>{node.unit}{node.unit_category ? ` (${node.unit_category})` : ''}</title>
                      </g>
                    );
                  })}
                </svg>
              </CardContent>
            </Card>

            <Card className="border-gray-200" data-testid="routes-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Routes</CardTitle>
                <CardDescription>
                  {!fromUnit || !toUnit
                    ? 'Pick a From and a To unit above to see every route between them'
                    : graph.routes.length === 0
                      ? `No route the portal can use from ${fromUnit} to ${toUnit}`
                      : `${graph.routes.length} route${graph.routes.length !== 1 ? 's' : ''} from ${fromUnit} to ${toUnit}. The portal tries the unit conversions alone first, then the category conversions in order, and uses the first that works. "Shortest" has the fewest steps.`}
                </CardDescription>
              </CardHeader>
              {fromUnit && toUnit && unreachableMessage(graph, fromUnit, toUnit) && (
                <CardContent>
                  <Alert variant="destructive" data-testid="no-route-alert">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{unreachableMessage(graph, fromUnit, toUnit).title}</AlertTitle>
                    <AlertDescription>{unreachableMessage(graph, fromUnit, toUnit).text}</AlertDescription>
                  </Alert>
                </CardContent>
              )}
              {graph.routes.length > 0 && (
                <CardContent className="space-y-3">
                  {graph.routes.map((r, i) => (
                    <button
                      type="button"
                      key={i}
                      onClick={() => setSelectedRoute(i)}
                      className={`w-full text-left p-4 border rounded-lg transition-colors ${i === selectedRoute ? 'border-green-500 bg-green-50' : 'hover:bg-gray-50'}`}
                      data-testid={`route-${i}`}
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-medium">{routeTitle(r, i)}</span>
                        <span className="text-xs text-muted-foreground">{r.hops} step{r.hops !== 1 ? 's' : ''}</span>
                        {r.shortest && <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Shortest</span>}
                        {r.used_by_portal && <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Used by portal</span>}
                        <span className="ml-auto font-mono text-sm">1 {fromUnit.toUpperCase()} = {formatFactor(r.factor)} {toUnit.toUpperCase()}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1 text-sm">
                        <span className="font-medium">{r.steps[0]?.from ?? fromUnit.toUpperCase()}</span>
                        {r.steps.map((s, j) => (
                          <span key={j} className="flex items-center gap-1">
                            <span className={s.kind === 'category' ? 'text-orange-600 font-medium' : 'text-muted-foreground'}>
                              → ×{formatFactor(s.factor)}{s.kind === 'category' ? ' (category)' : ''} →
                            </span>
                            <span className="font-medium">{s.to}</span>
                          </span>
                        ))}
                        {r.steps.length === 0 && <span className="text-muted-foreground">same unit, factor 1</span>}
                      </div>
                    </button>
                  ))}
                </CardContent>
              )}
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
